/* eslint-disable @angular-eslint/no-output-on-prefix */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import { NzTabChangeEvent } from 'ng-zorro-antd/tabs';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { SessionsService } from 'projects/app-core/src/app/session/service/sessions.service';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';
import { GridResponse } from 'projects/app-core/src/models/gridResponse';
import { FileUtilsService } from 'projects/app-core/src/service/fileService';
import { UtilsService } from 'projects/app-core/src/service/utils.service';
import { lastValueFrom, Observable } from 'rxjs';
import {
  AnyoRewardTypes,
  PaymentStatus,
  TransactionDirection,
} from '../../../models/expertRewardGridResponse';
import { ExpertsGridRequest } from '../../../models/expertsGridRequest';
import {
  AnyoRewardLedgerBaseGridData,
  AnyoRewardsBaseFilter,
  AnyoRewardsBaseGridRequest,
  gridFilterOption,
} from '../../../models/ledger/baseLedgerReqRes';
import { ExpertsService } from '../../../services/experts.service';
import {
  AdminAddRewardToLedger,
  AdminMakePayoutToExpertsRequest,
  ConsolidatedRewardExportPayload,
} from '../../../services/rewards.service';

export interface RewardsGridConfig {
  showExportButton?: boolean;
  showNewExportButton?: boolean; // Show new format export with post counts
  showOldExportButton?: boolean; // Show old format export for backward compatibility
  showResetButton?: boolean;
  showDateFilter?: boolean;
  showConsolidateExportButton?: boolean;
  showTabs?: boolean;
  defaultTab?: 'Complete' | 'Pending' | 'All';
  enablePayments?: boolean;
  enableAddReward?: boolean;
  enableLedgerView?: boolean;
  showSessionDetails?: boolean;
  customColumns?: string[];
  pageSize?: number;
  pageSizeOptions?: number[];
  tableType?: RewardTableType;
}

export interface RewardsGridPayload {
  expertName: string;
  duration?: string;
  totalAmount: number;
  paidOn: string;
  transactionId: string;
  modeOfpayment: string;
  adjustedAmount?: number;
}

// Session Rewards specific export payload with 14 columns
export interface SessionRewardsExportPayload {
  expertName: string;
  fullSessionsCompleted: number;
  halfSessionsCompleted: number;
  introductorySessionsCompleted: number;
  fullPricePerSession: number;
  halfPricePerSession: number;
  introductoryPricePerSession: number;
  fullSessionAmount: number;
  halfSessionAmount: number;
  introductorySessionAmount: number;
  coupleSessionsCount?: number;
  couplePricePerSession?: number;
  coupleSessionAmount?: number;
  totalPayable: number;
  adjustedAmount?: number;
  accountNumber: string;
  ifscCode: string;
}

// Expert Rewards specific export payload with post counts
export interface ExpertRewardsExportPayload {
  expertName: string;
  textPostsCount: number;
  imagePostsCount: number;
  videoPostsCount: number;
  totalAmount: number;
  accountNo: string;
  ifscCode: string;
  bankName: string;
}

export enum RewardTableType {
  SESSION_REWARDS = 'SESSION_REWARDS',
  CIRCLE_REWARDS = 'CIRCLE_REWARDS',
}

// Generic service interface for the rewards grid
export interface RewardsGridService<
  TRequest extends AnyoRewardsBaseGridRequest<any>,
  TResponse extends GridResponse<any, any>,
> {
  rewardGrid(request: TRequest): Observable<TResponse>;
  makePayout(request: AdminMakePayoutToExpertsRequest): Observable<any>;
  addRewardToLedger(request: AdminAddRewardToLedger): Observable<any>;
  getAmountForPayment(
    toData: number,
    userId: string,
  ): Observable<{ amount: number }>;
  consolidateRewardsExport(
    fromDate: number,
    toDate: number,
  ): Observable<ConsolidatedRewardExportPayload>;
}

@Component({
  selector: 'app-rewards-grid',
  templateUrl: './rewards-grid.component.html',
  styleUrl: './rewards-grid.component.scss',
})
export class RewardsGridComponent<
    T,
    F,
    TRequest extends AnyoRewardsBaseGridRequest<F>,
    TResponse extends GridResponse<
      gridFilterOption,
      AnyoRewardLedgerBaseGridData<T>
    >,
  >
  extends AnyoComponent
  implements OnInit
{
  @Input() config: RewardsGridConfig = {
    showExportButton: true,
    showResetButton: true,
    showDateFilter: true,
    showConsolidateExportButton: false,
    showTabs: true,
    defaultTab: 'Complete',
    enablePayments: true,
    enableAddReward: true,
    enableLedgerView: true,
    pageSize: 10,
    pageSizeOptions: [10, 20, 50, 100],
    tableType: RewardTableType.SESSION_REWARDS,
  };

  @Input() title: string = 'Manage Experts Rewards';
  @Input() customGridData: AnyoRewardLedgerBaseGridData<T>[] = [];
  @Input() useCustomData: boolean = false;
  @Input() customFilters: Partial<Record<AnyoRewardsBaseFilter & F, string[]>> =
    {};
  @Input() rewardsService?: RewardsGridService<TRequest, TResponse>;

  @Output() onExport = new EventEmitter<RewardsGridPayload[]>();
  @Output() onPayment = new EventEmitter<AdminMakePayoutToExpertsRequest>();
  @Output() onAddReward = new EventEmitter<AdminAddRewardToLedger>();
  @Output() onViewLedger = new EventEmitter<AnyoRewardLedgerBaseGridData<T>>();
  @Output() onDataChange = new EventEmitter<
    AnyoRewardLedgerBaseGridData<T>[]
  >();
  @Output() onFilterChange = new EventEmitter<any>();

  readonly transactionFilterOption = [
    TransactionDirection.CREDIT,
    TransactionDirection.DEBIT,
  ];
  readonly rewardTypeFilter = [
    AnyoRewardTypes.NEW_IMAGE_POST,
    AnyoRewardTypes.NEW_VIDEO_POST,
    AnyoRewardTypes.NEW_TEXT_POST,
    AnyoRewardTypes.DELETE_POST,
    AnyoRewardTypes.EVENT_PAYOUT,
  ];

  pageSize = 10;
  currentPage = 1;
  totalRecords = 0;
  spinner = false;
  editingRow: number | null = null;
  activeTab = 'Complete';
  gridData: AnyoRewardLedgerBaseGridData<T>[] = [];
  viewLedgerGridData: AnyoRewardLedgerBaseGridData<T>[] = [];
  isDrawerVisible = false;
  isPaymentModalVisible = false;
  paymentForm!: FormGroup;
  userId: string = '';
  amount: number = 0;
  dateRange: Date[] = [];
  consolidateExportDateRange: Date[] = [];
  isConsolidateExportModalVisible = false;
  consolidateExportLoading = false;
  dateFormat = 'dd/MM/yyyy';
  selectedTransactionType: TransactionDirection | null = null;
  selectedUserId: { userName: string; userId: string } | null = null;
  selectedRewardType: string | null = null;
  nameFilterOption: gridFilterOption = {} as gridFilterOption;
  addRewardForm!: FormGroup;
  isAddRewardModalVisible: boolean = false;
  isAddRewardLoading: boolean = false;
  expertOptions: { userName: string; userId: string }[] = [];
  includeTestExperts: boolean = false;

  constructor(
    auth: AuthService,
    private toastService: ToastService,
    public utils: UtilsService,
    private fb: FormBuilder,
    private fileUtil: FileUtilsService,
    private sessionService: SessionsService,
    private expertsService: ExpertsService,
  ) {
    super(auth);
    this.initializeForms();
  }

  override ngOnInit(): void {
    this.applyConfig();
    if (!this.useCustomData) {
      this.ready();
    } else {
      this.gridData = this.customGridData;
      this.totalRecords = this.customGridData.length;
    }
    this.paymentForm.get('toDate')?.valueChanges.subscribe((date) => {
      this.onToDateChanged(date);
    });
  }

  private initializeForms(): void {
    this.paymentForm = this.fb.group({
      amount: [null, [Validators.required, Validators.min(1)]],
      transactionId: [null, Validators.required],
      modeOfPayment: [null, Validators.required],
      fromDate: [{ value: null, disabled: true }, [Validators.required]],
      toDate: [null, [Validators.required]],
    });

    this.addRewardForm = this.fb.group({
      amount: [null, [Validators.required]],
      transactionDirection: [null, [Validators.required]],
      description: [null, [Validators.required]],
    });
  }

  private applyConfig(): void {
    this.pageSize = this.config.pageSize || 10;
    this.activeTab = this.config.defaultTab || 'Complete';
  }
  async exportViewLedger() {
    const exportData = this.prepareExportData(this.viewLedgerGridData, false);
    try {
      this.fileUtil.exportCSV<RewardsGridPayload>(
        exportData,
        `expert_reward_view_ledger_export_${moment().format('dd/mm/yyy hh:mm a')}`,
      );
      this.spinner = false;
    } catch (e) {
      this.toastService.showError('Export Error');
      this.spinner = false;
    }
  }

  async exportDataFn() {
    if (this.useCustomData) {
      const exportData = this.prepareExportData(this.gridData);
      this.onExport.emit(exportData);
      return;
    }

    if (!this.rewardsService) {
      this.toastService.showError('Service not available');
      return;
    }

    this.spinner = true;
    const reqBody = this.buildGridRequest(
      this.activeTab === 'Pending',
    ) as TRequest;
    reqBody.export = true;

    try {
      const gridResp = await lastValueFrom(
        this.rewardsService.rewardGrid(reqBody),
      );

      // Check data type
      const isSessionRewards = this.isSessionRewardsData(gridResp.data);
      const isExpertRewards = this.isExpertRewardsData(gridResp.data);

      if (isExpertRewards) {
        // Use expert rewards export with post counts
        let exportData = this.prepareExpertRewardsExportData(gridResp.data);

        // Sort alphabetically by expert name (default)
        exportData = this.sortExpertRewardsExport(exportData, 'alphabetical');

        // Transform data to use custom column headers
        const exportDataWithHeaders =
          this.transformExpertRewardsForExport(exportData);

        this.fileUtil.exportCSV<Record<string, any>>(
          exportDataWithHeaders,
          `expert_rewards_export_${moment().format('DD_MM_YYYY_hh_mm_a')}`,
        );
      } else if (isSessionRewards) {
        // Use session rewards export with 13 columns
        let exportData = this.prepareSessionRewardsExportData(gridResp.data);

        // Sort alphabetically by expert name (default)
        exportData = this.sortSessionRewardsExport(exportData, 'alphabetical');

        // Transform data to use custom column headers
        const exportDataWithHeaders =
          this.transformSessionRewardsForExport(exportData);

        this.fileUtil.exportCSV<Record<string, any>>(
          exportDataWithHeaders,
          `session_rewards_export_${moment().format('DD_MM_YYYY_hh_mm_a')}`,
        );
      } else {
        // Use regular export
        const exportData = this.prepareExportData(gridResp.data);
        this.fileUtil.exportCSV<RewardsGridPayload>(
          exportData,
          `expert_reward_export_${moment().format('DD_MM_YYYY_hh_mm_a')}`,
        );
      }

      this.spinner = false;
    } catch (e) {
      this.toastService.showError('Export Error');
      this.spinner = false;
    }
  }

  /**
   * Export data in old format (for backward compatibility)
   * Always uses the standard export format without post counts
   */
  async exportOldFormatFn() {
    if (this.useCustomData) {
      const exportData = this.prepareExportData(this.gridData);
      this.onExport.emit(exportData);
      return;
    }

    if (!this.rewardsService) {
      this.toastService.showError('Service not available');
      return;
    }

    this.spinner = true;
    const reqBody = this.buildGridRequest(
      this.activeTab === 'Pending',
    ) as TRequest;
    reqBody.export = true;

    try {
      const gridResp = await lastValueFrom(
        this.rewardsService.rewardGrid(reqBody),
      );

      // Always use old/regular export format
      const exportData = this.prepareExportData(gridResp.data);
      this.fileUtil.exportCSV<RewardsGridPayload>(
        exportData,
        `expert_reward_old_format_${moment().format('DD_MM_YYYY_hh_mm_a')}`,
      );

      this.spinner = false;
    } catch (e) {
      this.toastService.showError('Export Error');
      this.spinner = false;
    }
  }

  /**
   * Sort session rewards export data
   * @param data Export data to sort
   * @param sortBy 'alphabetical' or 'amount'
   */
  private sortSessionRewardsExport(
    data: SessionRewardsExportPayload[],
    sortBy: 'alphabetical' | 'amount' = 'alphabetical',
  ): SessionRewardsExportPayload[] {
    if (sortBy === 'alphabetical') {
      // Sort by expert name (A-Z)
      return data.sort((a, b) =>
        a.expertName.toLowerCase().localeCompare(b.expertName.toLowerCase()),
      );
    } else {
      // Sort by total payable (highest to lowest)
      return data.sort((a, b) => b.totalPayable - a.totalPayable);
    }
  }

  /**
   * Transform session rewards data to use custom column headers
   * Maps property names to user-friendly column names for CSV export
   */
  private transformSessionRewardsForExport(
    data: SessionRewardsExportPayload[],
  ): Record<string, any>[] {
    return data.map((item) => ({
      'Expert Name': item.expertName,
      'No of Full sessions completed': item.fullSessionsCompleted,
      'No. of half sessions completed': item.halfSessionsCompleted,
      'No. of Introductory sessions completed':
        item.introductorySessionsCompleted,
      'No.of Couple Session (61-90)min': item.coupleSessionsCount,
      'Fullprice per session': item.fullPricePerSession,
      'Half price per session': item.halfPricePerSession,
      'Introductory price per session': item.introductoryPricePerSession,
      '(61-90)min price per session': item.couplePricePerSession,
      'Full session amount': item.fullSessionAmount,
      'Half session amount': item.halfSessionAmount,
      'Introductory session amount': item.introductorySessionAmount,
      'Couple therapy amount': item.coupleSessionAmount,
      'Manually Updated Session Amount': item.adjustedAmount,
      'Total payable': item.totalPayable,
      'Account Number': item.accountNumber,
      'IFSC Code': item.ifscCode,
    }));
  }

  private prepareExportData(
    data: AnyoRewardLedgerBaseGridData<T>[],
    includeDuration: boolean = true,
  ): RewardsGridPayload[] {
    return data.map((value) => {
      const base = {
        expertName: value.userName,
        totalAmount: this.getAmount(value),
        paidOn: this.utils.convertEpochToFormattedDate(value.date),
        transactionId: value.transactionId,
        modeOfpayment: value.modeOfPayment,
      } as RewardsGridPayload;

      if (includeDuration && value.duration) {
        base.duration =
          this.utils.convertEpochToFormattedDate(value.duration.start) +
          ' - ' +
          this.utils.convertEpochToFormattedDate(value.duration.end);
      }

      return base;
    });
  }

  /**
   * Prepare session rewards export data with 14 columns
   * Removes name prefixes, formats amounts without Rs, and includes bank details
   */
  private prepareSessionRewardsExportData(
    data: AnyoRewardLedgerBaseGridData<T>[],
  ): SessionRewardsExportPayload[] {
    return data.map((value) => {
      const meta = value as any; // Cast to access SessionRewardsGridMeta fields

      // Remove prefixes (Ms, Mrs, Mr, Dr) from expert name
      const cleanName = this.removeNamePrefix(value.userName);

      // Extract separate account number and IFSC code
      const accountNumber = meta.bankDetails?.accountNo || '';
      const ifscCode = meta.bankDetails?.ifscCode || '';

      return {
        expertName: cleanName,
        fullSessionsCompleted: meta.fullSessionsCount || 0,
        halfSessionsCompleted: meta.halfSessionsCount || 0,
        introductorySessionsCompleted: meta.introductorySessionsCount || 0,
        fullPricePerSession: meta.fullPricePerSession || 0,
        halfPricePerSession: meta.halfPricePerSession || 0,
        introductoryPricePerSession: meta.introductoryPricePerSession || 0,
        fullSessionAmount: meta.fullSessionAmount || 0,
        halfSessionAmount: meta.halfSessionAmount || 0,
        introductorySessionAmount: meta.introductorySessionAmount || 0,
        coupleSessionsCount: meta.coupleSessionsCount || 0,
        couplePricePerSession: meta.couplePricePerSession || 0,
        coupleSessionAmount: meta.coupleSessionAmount || 0,
        totalPayable: meta.totalPayable || 0,
        adjustedAmount: meta.adjustedAmount || 0,
        accountNumber: accountNumber,
        ifscCode: ifscCode,
      };
    });
  }

  /**
   * Remove name prefixes (Ms, Mrs, Mr, Dr) from expert name
   */
  private removeNamePrefix(name: string): string {
    // Regex to match Ms, Mrs, Mr, Dr with optional period and space
    return name.replace(/^(Ms\.?|Mrs\.?|Mr\.?|Dr\.?)\s+/i, '').trim();
  }

  /**
   * Check if the data contains session rewards metadata
   */
  private isSessionRewardsData(
    data: AnyoRewardLedgerBaseGridData<T>[],
  ): boolean {
    if (data.length === 0) return false;
    const firstItem = data[0] as any;
    return (
      firstItem.fullSessionsCount !== undefined ||
      firstItem.halfSessionsCount !== undefined ||
      firstItem.introductorySessionsCount !== undefined ||
      firstItem.coupleSessionsCount !== undefined
    );
  }

  /**
   * Check if the data contains expert rewards (post-based)
   */
  private isExpertRewardsData(
    data: AnyoRewardLedgerBaseGridData<T>[],
  ): boolean {
    if (data.length === 0) return false;
    const firstItem = data[0] as any;
    // Expert rewards have postId and description with post types
    return (
      firstItem.postId !== undefined ||
      (firstItem.description &&
        (firstItem.description === AnyoRewardTypes.NEW_TEXT_POST ||
          firstItem.description === AnyoRewardTypes.NEW_IMAGE_POST ||
          firstItem.description === AnyoRewardTypes.NEW_VIDEO_POST ||
          firstItem.description === AnyoRewardTypes.DELETE_POST))
    );
  }

  /**
   * Prepare expert rewards export data using backend-provided post counts
   */
  private prepareExpertRewardsExportData(
    data: AnyoRewardLedgerBaseGridData<T>[],
  ): ExpertRewardsExportPayload[] {
    return data.map((item) => {
      const meta = item as any; // Cast to access ExpertRewardsGridMeta fields
      const cleanName = this.removeNamePrefix(item.userName);
      return {
        expertName: cleanName,
        textPostsCount: meta.noOfTextPosts || 0,
        imagePostsCount: meta.noOfImagePosts || 0,
        videoPostsCount: meta.noOfVideoPosts || 0,
        totalAmount: this.getAmount(item),
        accountNo: meta.bankDetails?.accountNo || '',
        ifscCode: meta.bankDetails?.ifscCode || '',
        bankName: meta.bankDetails?.bankName || '',
      };
    });
  }

  /**
   * Transform expert rewards data to use custom column headers
   */
  private transformExpertRewardsForExport(
    data: ExpertRewardsExportPayload[],
  ): Record<string, any>[] {
    return data.map((item) => ({
      'Expert name': item.expertName,
      'No of text posts': item.textPostsCount,
      'No of image posts': item.imagePostsCount,
      'No of video posts': item.videoPostsCount,
      'Total Amount Pending': item.totalAmount,
      'Bank Name': item.bankName,
      'Account Number': item.accountNo,
      'IFSC Code': item.ifscCode,
    }));
  }

  /**
   * Sort expert rewards export data
   */
  private sortExpertRewardsExport(
    data: ExpertRewardsExportPayload[],
    sortBy: 'alphabetical' | 'amount' = 'alphabetical',
  ): ExpertRewardsExportPayload[] {
    if (sortBy === 'alphabetical') {
      return data.sort((a, b) =>
        a.expertName.toLowerCase().localeCompare(b.expertName.toLowerCase()),
      );
    } else {
      return data.sort((a, b) => b.totalAmount - a.totalAmount);
    }
  }
  getAmount(value: AnyoRewardLedgerBaseGridData<T>): number {
    switch (this.activeTab) {
      case 'Pending':
        return value.creditValue === 0 ? value.debitValue : value.creditValue;

      case 'Completed':
        return value.debitValue < 0 ? -value.debitValue : value.debitValue;

      default:
        return value.debitValue < 0 ? value.debitValue : value.creditValue;
    }
  }

  async ready(): Promise<void> {
    if (this.user && !this.useCustomData) {
      await this.getAllOrPendingGridData();
      await this.getExperts();
    }
  }

  async getAllOrPendingGridData(pending: boolean = false) {
    if (this.useCustomData) {
      this.gridData = this.customGridData;
      this.onDataChange.emit(this.gridData);
      return;
    }

    if (!this.rewardsService) {
      this.toastService.showError('Service not available');
      return;
    }

    this.spinner = true;
    const reqBody = this.buildGridRequest(pending) as TRequest;
    this.rewardsService.rewardGrid(reqBody).subscribe({
      next: (res: TResponse) => {
        this.gridData = res.data;
        this.totalRecords = res.totalRecords;
        const filters = res.filterOptions.userInfo.sort((a, b) =>
          a.userName
            .trim()
            .toLowerCase()
            .localeCompare(b.userName.trim().toLowerCase()),
        );

        this.nameFilterOption = { userInfo: filters };
        this.expertOptions = filters.map((u) => ({
          userId: u.userId,
          userName: u.userName,
        }));
        this.onDataChange.emit(this.gridData);
        this.getExperts();
      },
      error: (e: any) => {
        const error = e.error as IAnyoError;
        this.toastService.showError(error.description);
      },
      complete: () => {
        this.spinner = false;
      },
    });
  }

  buildGridRequest(pending: boolean): any {
    const reqBody: any = {
      page: this.currentPage,
      pageSize: this.pageSize,
      filters: {
        TRANSACTION_TYPE: [],
        DESCRIPTION: [],
        PAYMENT_SORT: [],
        CREDIT: [],
        DEBIT: [],
        USER_ID: [],
        TEST_FILTER: [],
        ...this.customFilters,
      },
      export: false,
    };

    if (pending) reqBody.filters.PAYMENT_SORT = [PaymentStatus.Pending];
    if (this.dateRange.length > 0) {
      reqBody.dateFilters = {
        TRANSFER_DATE: {
          from: moment(this.dateRange[0], 'x').startOf('day').toDate(),
          to: moment(this.dateRange[1], 'x').endOf('day').toDate(),
        },
      };
    }
    if (this.activeTab === 'Complete')
      reqBody.filters.PAYMENT_SORT = [PaymentStatus.Success];
    if (this.selectedTransactionType)
      reqBody.filters[
        this.selectedTransactionType === TransactionDirection.CREDIT
          ? 'CREDIT'
          : 'DEBIT'
      ] = ['true'];
    if (this.selectedUserId)
      reqBody.filters.USER_ID = [this.selectedUserId.userId];
    if (this.selectedRewardType)
      reqBody.filters.DESCRIPTION = [this.selectedRewardType];

    reqBody.filters.TEST_FILTER = [this.includeTestExperts ? 'true' : 'false'];

    this.onFilterChange.emit(reqBody);
    return reqBody;
  }

  async openDrawer(id: AnyoRewardLedgerBaseGridData<T>): Promise<void> {
    this.isDrawerVisible = true;
    if (this.config.enableLedgerView) {
      this.onViewLedger.emit(id);
      if (!this.useCustomData) {
        await this.getLedgerGridData(id);
      }
    }
  }

  closeDrawer(): void {
    this.isDrawerVisible = false;
  }

  async getLedgerGridData(
    data: AnyoRewardLedgerBaseGridData<T>,
  ): Promise<void> {
    if (!this.rewardsService) {
      this.toastService.showError('Service not available');
      return;
    }

    this.spinner = true;
    const reqBody: any = {
      page: 1,
      pageSize: 100,
      filters: {
        TRANSACTION_TYPE: [],
        DESCRIPTION: [],
        PAYMENT_SORT: [],
        CREDIT: [],
        DEBIT: [],
        USER_ID: [data.userId],
      },
      dateFilters: {
        TRANSFER_DATE: {
          from: moment(data.duration?.start, 'x').startOf('day').toDate(),
          to: moment(data.duration?.end, 'x').endOf('day').toDate(),
        },
      },
      export: false,
    };

    return new Promise<void>((resolve, reject) => {
      this.rewardsService!.rewardGrid(reqBody as TRequest).subscribe({
        next: (res: any) => {
          const rawData = res.data.filter((d: any) => d.creditValue >= 0);
          this.viewLedgerGridData = this.enrichLedgerData(rawData);
          resolve();
        },
        error: (e: any) => {
          const error = e.error as IAnyoError;
          this.toastService.showError(error.description);
          reject(e);
        },
        complete: () => {
          this.spinner = false;
        },
      });
    });
  }

  onTabChange(event: NzTabChangeEvent): void {
    this.activeTab = event.tab.nzTitle;
    this.resetFilters();
    this.getAllOrPendingGridData(this.activeTab === 'Pending');
  }

  resetFilters(): void {
    this.dateRange = [];
    this.selectedTransactionType = null;
    this.selectedUserId = null;
    this.selectedRewardType = null;
    this.currentPage = 1;
  }

  async resetAllFilter() {
    this.resetFilters();
    await this.getAllOrPendingGridData(this.activeTab === 'Pending');
  }

  async resetFilterAndFetchData(
    filterName: 'TRANSACTION_TYPE' | 'USER_ID' | 'REWARD_TYPE',
  ) {
    switch (filterName) {
      case 'TRANSACTION_TYPE':
        this.selectedTransactionType = null;
        break;
      case 'USER_ID':
        this.selectedUserId = null;
        break;
      case 'REWARD_TYPE':
        this.selectedRewardType = null;
        break;
    }
    await this.getAllOrPendingGridData(this.activeTab === 'Pending');
  }

  async onSubmitFilterAndFetchData() {
    await this.getAllOrPendingGridData(this.activeTab === 'Pending');
  }

  async getExperts() {
    const request: ExpertsGridRequest = {
      page: 1,
      pageSize: 100,
      export: false,
      filters: {
        NAME: undefined,
        ROLES: undefined,
        USER_INTEREST: undefined,
        STATUS: undefined,
        TEST: [this.includeTestExperts ? 'true' : 'false'],
      },
    };
    this.expertsService.listExperts(request).subscribe({
      next: (value) => {
        this.expertOptions = value.data
          .map((expert) => ({ userId: expert.userId, userName: expert.name }))
          .sort((a, b) =>
            a.userName
              .trim()
              .toLowerCase()
              .localeCompare(b.userName.trim().toLowerCase()),
          );
      },
      error: (error) => {
        const errorBody = error.error as IAnyoError;
        this.toastService.showError(errorBody.description);
      },
    });
  }

  async toggleIncludeTestExperts($event: boolean) {
    this.includeTestExperts = $event;
    await this.getExperts();
  }

  openPaymentModal(
    userId: string,
    date?: { start: string; end: string },
  ): void {
    if (!this.config.enablePayments) return;
    this.paymentForm.patchValue({
      fromDate: date ? moment(date.start, 'x').startOf('day').toDate() : null,
    });
    // Use start of day so the same calendar day remains selectable
    this.lastPayoutDate = date
      ? moment(date.start, 'x').startOf('day').toDate()
      : null;
    this.userId = userId;
    this.isPaymentModalVisible = true;
  }

  closePaymentModal(): void {
    this.isPaymentModalVisible = false;
    this.paymentForm.reset();
  }

  openAddRewardModal(id: string): void {
    if (!this.config.enableAddReward) return;

    this.isAddRewardModalVisible = true;
    this.userId = id;
  }

  closeAddRewardModal(): void {
    this.isAddRewardModalVisible = false;
    this.addRewardForm.reset();
  }

  openConsolidateExportModal(): void {
    this.consolidateExportDateRange = [];
    this.isConsolidateExportModalVisible = true;
  }

  closeConsolidateExportModal(): void {
    this.isConsolidateExportModalVisible = false;
    this.consolidateExportDateRange = [];
  }

  disableConsolidateDatesAfterToday = (current: Date): boolean =>
    moment(current).startOf('day').isAfter(moment().startOf('day'));

  async submitConsolidateExport(): Promise<void> {
    if (!this.rewardsService || this.consolidateExportDateRange.length !== 2) {
      return;
    }
    const [start, end] = this.consolidateExportDateRange;
    const fromDate = moment(start).startOf('day').valueOf();
    const toDate = moment(end).endOf('day').valueOf();
    this.consolidateExportLoading = true;
    try {
      const payload = await lastValueFrom(
        this.rewardsService.consolidateRewardsExport(fromDate, toDate),
      );
      this.downloadConsolidatedRewardsCsv(payload, fromDate, toDate);
      this.toastService.showSuccess('Consolidated CSV downloaded');
      this.closeConsolidateExportModal();
    } catch (e: unknown) {
      const err = e as { error?: IAnyoError };
      const msg = err.error?.description || 'Export failed';
      this.toastService.showError(msg);
    } finally {
      this.consolidateExportLoading = false;
    }
  }

  private downloadConsolidatedRewardsCsv(
    payload: ConsolidatedRewardExportPayload,
    fromMs: number,
    toMs: number,
  ): void {
    const fileName = `consolidated_rewards_${moment(fromMs).format('YYYY-MM-DD')}_${moment(toMs).format('YYYY-MM-DD')}_${moment().format('DD_MM_YYYY_hh_mm_a')}`;
    this.fileUtil.exportCSV(payload.rows, fileName);
  }

  async submitAddRewardForm(): Promise<void> {
    if (this.addRewardForm.valid) {
      this.isAddRewardLoading = true;
      const { amount, transactionDirection, description } =
        this.addRewardForm.value;
      const reqBody: AdminAddRewardToLedger = {
        userId: this.userId,
        amount: amount,
        paymentTransactionType: transactionDirection,
        description: description,
      };

      this.onAddReward.emit(reqBody);
    }
  }

  submitPaymentForm(): void {
    if (this.paymentForm.valid) {
      const { transactionId, modeOfPayment, amount, fromDate, toDate } =
        this.paymentForm.getRawValue();
      const reqBody: AdminMakePayoutToExpertsRequest = {
        userId: this.userId,
        amount: amount,
        transactionId,
        modeOfPayment,
        fromDate: moment(fromDate, 'x').startOf('day').valueOf(),
        toDate: moment(toDate, 'x').endOf('day').valueOf(),
      };
      this.onPayment.emit(reqBody);
    }
  }

  async nzPageIndexChange($event: number) {
    this.currentPage = $event;
    await this.getAllOrPendingGridData(this.activeTab === 'Pending');
  }

  async nzPageSizeChange($event: number) {
    this.pageSize = $event;
    await this.getAllOrPendingGridData();
  }

  async dateChange($event: Date[]) {
    this.dateRange = $event;
    await this.getAllOrPendingGridData(this.activeTab === 'Pending');
  }

  // Public methods for external control
  public refreshData(): void {
    this.getAllOrPendingGridData(this.activeTab === 'Pending');
  }

  public updateCustomData(data: AnyoRewardLedgerBaseGridData<T>[]): void {
    if (this.useCustomData) {
      this.customGridData = data;
      this.gridData = data;
      this.totalRecords = data.length;
    }
  }

  public setLedgerData(data: AnyoRewardLedgerBaseGridData<T>[]): void {
    this.viewLedgerGridData = this.enrichLedgerData(data);
  }
  lastPayoutDate: Date | null = null;

  disableFutureDates = (current: Date): boolean => {
    const today = moment().startOf('day');
    if (!this.lastPayoutDate) {
      return moment(current).isAfter(today, 'day');
    }
    const minInclusive = moment(this.lastPayoutDate).startOf('day');
    return (
      moment(current).isBefore(minInclusive, 'day') ||
      moment(current).isSameOrAfter(today, 'day')
    );
  };

  onToDateChanged(date: any): void {
    const fromDate = this.paymentForm.get('fromDate')?.value;
    if (fromDate && date) {
      const toDate = moment(date, 'x').endOf('day').valueOf();
      this.rewardsService?.getAmountForPayment(toDate, this.userId).subscribe({
        next: (res: { amount: number }) => {
          this.paymentForm.get('amount')?.setValue(res.amount);
        },
      });
    }
  }

  sessionTags: { label: string; value: string }[] = [];
  getSessionDetailsText(data: any): string {
    const details = data.sessionDetails;
    if (!details) return 'N/A';

    const { userName, sessionMinutes, sessionStart, sessionEnd, partnerName } =
      details;

    const date = moment(sessionStart).format('DD/MM/YYYY');
    const startTime = moment(sessionStart).format('hh:mm A');
    const endTime = moment(sessionEnd).format('hh:mm A');

    return `User Name: ${userName}<br>
          Session minutes: ${sessionMinutes} mins<br>
          Date: ${date}<br>
          Session Time: ${startTime} - ${endTime}<br>
          Partner Name: ${partnerName}`;
  }

  private enrichLedgerData(
    data: AnyoRewardLedgerBaseGridData<T>[],
  ): AnyoRewardLedgerBaseGridData<T>[] {
    return data.map((item) => {
      const details = (item as any).sessionDetails;
      if (!details) return item;

      const tags = (details.tags || []).map((t: string) => ({
        label: this.utils.convertToTitleCase(t),
        value: this.utils.getRandomColor(),
      }));

      return {
        ...item,
        sessionTags: tags,
      } as any;
    });
  }

  navigateToSessions(text: string) {
    const match = text.match(/[a-f0-9]{24}/); // MongoDB ObjectId format
    const id2 = match ? match[0] : null;
    const gridRequest = this.sessionService.buildSessionGridRequest();
    if (id2) {
      gridRequest.globalSearch = [id2];
    }
    const encoded = this.sessionService.encodeGridRequestToBase64(gridRequest);
    window.open(`/therapist/session/admin?gridRequest=${encoded}`);
  }
}
